import React, { useState } from "react";
import { useSearchQuery, usePagination } from "../../../hooks";
import { AdminSharedHeader, AdminSharedTable, AdminSharedDeleteModal } from "../Shared";
import { useQuery, useDelete } from "../../../hooks";
import { useGetDisplayConfigsQuery, useDeleteDisplayConfigMutation } from "../services";
import { DisplayConfigurationsForm } from "./Form";
import { getLocalizedString } from "../../../shared/translation";
import { DetailsDropDownView, TableDropDownView } from "../../ManagementPortal/Shared";
import { TickOrCross } from "../../Shared";
import { Link } from "react-router-dom";
import { useGetVariablesQuery } from "../services";
import { QIModal, QIModalBody, QIModalHeader } from "../../../components";

export const DisplayConfigurationsContainer = () => {
  const [idToDelete, setIdToDelete] = useState(null);
  const [idToEdit, setIdToEdit] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showVariableModal, setVariableModal] = useState(false);
  const [variableModalData, setVariableModalData] = useState([]);
  const { page, setPage, perPage, pagination } = usePagination();

  // Local state for variables modal pagination (not connected to URL params)
  const [variablePage, setVariablePage] = useState(1);
  const [variablePerPage, setVariablePerPage] = useState(20);

  const { setSimpleSearch, setAdvanceSearch, q } = useSearchQuery({
    page,
    setPage,
    simpleSearchKey: "name_cont",
  });

  //Capitalize first letter
  function capitalizeFirstLetter(str) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const queryParams = {
    page: page,
    per_page: perPage,
    order_by: "display_configurations.created_at",
    order_dir: "desc",
  };

  const headers = [
    { label: "Display Name", key: "name", className: "display_name" },
    { label: "Map", key: "map", className: "map" },
    {
      label: getLocalizedString("map_key", "API Key"),
      type: "component",
      className: "api-key more",
      component: ({ data }) => {
        return <DetailsDropDownView data={data?.map_key} />;
      },
    },
    {
      label: "Tracking",
      type: "component",
      className: "tracking",
      component: ({ data }) => {
        return capitalizeFirstLetter(data?.default_tracking_portal_landing);
      },
    },
    {
      label: "Dark Mode",
      nestedValue: true,
      getNestedValue: ({ dark_mode }) => {
        return <TickOrCross flag={dark_mode} />;
      },
      className: "enabled",
    },
    {
      label: "Variables",
      type: "component",
      className: "variables",
      component: ({ data }) => {
        let variables = Array.isArray(data?.variables)
          ? data.variables.map((value) => {
              return value?.id;
            })
          : [];

        const getVariables = (index) => {
          return data?.variables?.find((value) => value?.id === index);
        };

        let selectedVariables = variablesList?.data
          ?.map((variable) => {
            if (variables?.includes(variable?.id)) {
              return {
                name: variable?.display_name,
                precision: getVariables(variable?.id)?.precision,
              };
            }
          })
          .filter(Boolean);
        return (
          <>
            <Link
              onClick={() => {
                setVariableModal(true);
                setVariableModalData(selectedVariables);
                // Reset variable pagination to first page when opening modal
                setVariablePage(1);
              }}
              style={{ textDecoration: "none" }}
            >
              {variables?.length}
            </Link>
          </>
        );
      },
    },
  ];

  const variableHeaders = [
    { label: "Display Name", key: "name", className: "display_name" },
    { label: "Precision", key: "precision", className: "precision" },
  ];

  /* API Calls */

  // Get data for target variables list
  const { data: variablesList, isLoading } = useGetVariablesQuery({
    per_page: 2000,
    order_by: "variables.display_name",
    classification_type: ["tracking_data_device", "tracking_data_custom"],
  });

  const {
    data = { data: [], total_count: 0 },
    isFetching,
    isSuccess,
    error,
  } = useGetDisplayConfigsQuery({
    ...queryParams,
    q,
  });

  const {
    deleteItem: deleteDisplayConfiguration,
    deleteErrorMsg,
    setDeleteErrorMsg,
  } = useDelete({
    deleteMutation: useDeleteDisplayConfigMutation,
    closeModal: () => setShowDeleteWarning(false),
  });

  /* Api Calls End */

  /* Actions */
  const createNewHandler = () => {
    setShowForm(true);
    setIdToEdit(null);
  };

  const deleteClickHandler = (datum) => {
    setShowDeleteWarning(true);
    setIdToDelete(datum.id);
  };

  const editClickHandler = (datum) => {
    setShowForm(true);
    setIdToEdit(datum.id);
  };

  // Get paginated variable data for the modal
  const getPaginatedVariableData = () => {
    const startIndex = (variablePage - 1) * variablePerPage;
    const endIndex = startIndex + variablePerPage;
    return variableModalData.slice(startIndex, endIndex);
  };

  // Create a custom pagination object for the variables modal
  const variablePagination = {
    page: variablePage,
    perPage: variablePerPage,
    count: variableModalData?.length || 0,
    onPageChange: setVariablePage,
    onPerPageChange: setVariablePerPage,
  };

  const getDeleteItemName = (id) => {
    let name = "";
    const index = data?.data?.findIndex((datum) => datum.id === id);
    if (index > -1) {
      name = data?.data[index].name;
    }

    return name;
  };

  return (
    <>
      <>
        <article className="main-container">
          <AdminSharedHeader
            createNewHandler={createNewHandler}
            groupName="other"
            heading="Configurations"
            handleSearchKey={setSimpleSearch}
            simpleSearchPlaceHolder="Search by Name"
          />
          <AdminSharedTable
            isLoading={isFetching}
            error={error}
            isSuccess={isSuccess}
            headers={headers}
            data={data?.data}
            pagination={{ ...pagination, count: data.total_count }}
            onDelete={deleteClickHandler}
            onEdit={editClickHandler}
            className="display"
            auditResource="DisplayConfiguration"
            auditKey="display"
          />
        </article>

        {showForm && (
          <DisplayConfigurationsForm idToEdit={idToEdit} closeForm={() => setShowForm(false)} />
        )}

        <AdminSharedDeleteModal
          show={showDeleteWarning}
          resourceName="Display"
          error={deleteErrorMsg}
          getDeleteItemName={() => getDeleteItemName(idToDelete)}
          onHide={() => {
            setDeleteErrorMsg();
            setShowDeleteWarning(false);
          }}
          onDelete={() => deleteDisplayConfiguration({ id: idToDelete })}
        />
        <QIModal show={showVariableModal} size={"large"}>
          <QIModalHeader onHide={() => setVariableModal(false)}>
            <h5>Variables</h5>
          </QIModalHeader>
          <QIModalBody>
            <AdminSharedTable
              isLoading={isLoading}
              pagination={variablePagination}
              isSuccess={true}
              data={getPaginatedVariableData()}
              headers={variableHeaders}
              className="display_config"
            />
          </QIModalBody>
        </QIModal>
      </>
    </>
  );
};
